/**
 * Kullanıcının seçtiği vurgu renginden marka ton merdivenini (--brand-400…700) türetir.
 *
 * Kullanıcıya tek bir renk sorulur; dört tonu elle seçtirmek hem zahmetli hem de tutarsız
 * sonuç veriyor (düğme, odak halkası ve seçili satır birbirini tutmuyor). Tonlar HSL
 * uzayında ton ve doygunluk korunarak yalnızca açıklık kaydırılarak üretilir — kullanıcının
 * seçtiği renk 500 tonudur, gördüğü şey odur.
 */

export interface BrandScale {
  400: string;
  500: string;
  600: string;
  700: string;
}

/**
 * Octo'nun marka mavisi — uygulamanın varsayılan vurgusu.
 *
 * Marka kılavuzundaki birincil renk (#3577FF). Daha önce varsayılan yeşildi ve kodda
 * "Renium yeşili" diye geçiyordu; yeşil Node görünümünün rengi, markanın değil.
 * Logonun gradyan uçları (#56D9FF → #E380FF) vurgu rengi olarak kullanılmıyor: ikisi de
 * açık tonlar ve beyaz metinle kontrastları yetersiz.
 */
export const DEFAULT_ACCENT = '#3577ff';

/** Ayarlarda sunulan hazır renkler. Hepsi hem açık hem koyu temada okunur tonlarda. */
export const ACCENT_PRESETS = [
  { name: 'octo', value: DEFAULT_ACCENT },
  { name: 'indigo', value: '#4040ff' },
  { name: 'emerald', value: '#10b981' },
  { name: 'violet', value: '#8b5cf6' },
  { name: 'rose', value: '#f43f5e' },
  { name: 'amber', value: '#f59e0b' },
  { name: 'teal', value: '#14b8a6' },
  { name: 'slate', value: '#64748b' },
] as const;

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Geçerli bir #rgb/#rrggbb mi? Geçersiz değer temayı bozmadan varsayılana düşsün diye. */
export function isValidHex(value: string | null | undefined): value is string {
  return !!value && HEX_RE.test(value.trim());
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.trim().slice(1);
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const to = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h, s, l];
}

export function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [channel(h + 1 / 3) * 255, channel(h) * 255, channel(h - 1 / 3) * 255];
}

/** Açıklığı `delta` kadar kaydırır (0–1 aralığında kırpılır); ton ve doygunluk korunur. */
function shiftLightness(hex: string, delta: number): string {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  const next = Math.max(0.06, Math.min(0.94, l + delta));
  return rgbToHex(hslToRgb([h, s, next]));
}

/**
 * Seçilen renk 500 tonudur. 400 bir adım açık (hover/vurgu), 600 ve 700 koyulaşır
 * (basılı düğme, koyu zeminde metin). Aradaki farklar gözle ayırt edilecek kadar büyük
 * ama renk kimliğini bozmayacak kadar küçük tutuldu.
 */
export function deriveBrandScale(accent: string): BrandScale {
  const base = isValidHex(accent) ? accent : DEFAULT_ACCENT;
  return {
    400: shiftLightness(base, +0.1),
    500: base.toLowerCase(),
    600: shiftLightness(base, -0.08),
    700: shiftLightness(base, -0.16),
  };
}
